import { z } from "zod";
import type { StepType } from "../../hooks/usePlay";

/**
 * Per-step-type Zod schemas mirroring Plan 5 (apps/api/src/services/steps/type-validators.ts).
 * Used inline by StepDetail components to validate the operator-supplied output
 * BEFORE we POST /complete. The server re-validates and is the source of truth;
 * this gives instant feedback in the UI.
 */
export const STEP_OUTPUT_SCHEMAS: Record<StepType, z.ZodType> = {
  decision: z.object({
    chosen_option_id: z.string().min(1, "Pick an option"),
    rationale: z.string().min(1, "Rationale is required"),
  }),
  comm: z.object({
    sent_at: z
      .string()
      .min(1, "When was it sent?")
      .refine((s) => !Number.isNaN(Date.parse(s)), "Invalid datetime"),
    receipt_ref: z.string().optional(),
  }),
  query: z.object({
    result_ref: z.string().min(1, "Where is the result?"),
    summary: z.string().optional(),
  }),
  micro_task: z.object({
    completed_items: z
      .array(z.string().min(1))
      .min(1, "Tick at least one item"),
  }),
  wait: z.object({
    woke_at: z.string().min(1).refine(
      (s) => !Number.isNaN(Date.parse(s)),
      "Invalid datetime",
    ),
    reason: z.string().min(1, "Why did the wait clear?"),
  }),
  milestone: z.object({
    completed_items: z.array(z.string().min(1)).min(1),
    notes: z.string().optional(),
  }),
  instrumentation: z.object({
    measured_value: z.coerce.number(),
    measured_at: z.string().optional(),
  }),
};

export interface ZodFieldError {
  path: string;
  message: string;
}

export function zodIssuesToErrors(err: z.ZodError): ZodFieldError[] {
  return err.issues.map((i) => ({
    path: i.path.join("."),
    message: i.message,
  }));
}
